import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { ApiError } from '../api';
import type { MemberStatus } from '../types';

/**
 * A small set of building blocks rather than a component library. Real
 * <button>, <label> and <table> elements throughout, so keyboard and screen
 * reader behaviour comes for free instead of being reimplemented badly.
 */

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <p className="muted" role="status">
      {label}…
    </p>
  );
}

export function Notice({
  kind = 'error',
  children,
}: {
  kind?: 'error' | 'success' | 'info' | 'warning';
  children: ReactNode;
}) {
  return (
    <p className={`notice notice-${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </p>
  );
}

/**
 * Turns a thrown error into something a volunteer can act on. The API is
 * careful about which failures carry a safe explanation, so anything else gets
 * a generic line rather than a leaked internal message.
 */
export function ErrorNotice({ error }: { error: unknown }) {
  if (error === null || error === undefined) return null;
  const message =
    error instanceof ApiError
      ? error.message
      : 'Something went wrong. Please try again.';
  return <Notice kind="error">{message}</Notice>;
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
}

export function Field({ label, hint, id, ...input }: FieldProps) {
  const fieldId = id ?? `field-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  return (
    <div className="field">
      <label htmlFor={fieldId}>{label}</label>
      <input id={fieldId} aria-describedby={hintId} {...input} />
      {hint ? (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  children: ReactNode;
}

export function SelectField({ label, id, children, ...select }: SelectFieldProps) {
  const fieldId = id ?? `field-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`;
  return (
    <div className="field">
      <label htmlFor={fieldId}>{label}</label>
      <select id={fieldId} {...select}>
        {children}
      </select>
    </div>
  );
}

export function StatusPill({ status }: { status: MemberStatus }) {
  return <span className={`pill pill-${status}`}>{status}</span>;
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <p className="empty-title">{title}</p>
      {children ? <p className="muted">{children}</p> : null}
    </div>
  );
}

/** Dates arrive as YYYY-MM-DD or a MySQL datetime. Show them the way people write them. */
export function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function fullName(member: {
  title: string | null;
  first_name: string;
  last_name: string;
}): string {
  return [member.title, member.first_name, member.last_name].filter(Boolean).join(' ');
}

const money = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
});

export function formatMoney(amount: number): string {
  return money.format(amount);
}
